import React from "react";
import { ReactComponent as IconSuccess } from "@assets/Icons/Icon_Success.svg";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";
import { ReactComponent as IconCross } from "@assets/Icons/IconCrossSquare.svg";

/**
 * Visual variant of the status indicator. Pick the variant by the
 * intent of the state, not by the literal label:
 *  - `success` — healthy / active / online / completed.
 *  - `warning` — partial / degraded / missing recipients / pending review.
 *  - `error`   — outage / failed / down.
 *  - `info`    — in-progress / informational / provisioning.
 *  - `neutral` — unknown / draft / inactive / "no data".
 */
export type StatusBadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral";

export type StatusBadgeSize = "sm" | "md";

interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  label: string;
  /**
   * Default `"sm"` — matches the historical `HealthBadge` look used in
   * tables. Use `"md"` for card headers / detail views where the badge
   * should read with more emphasis.
   */
  size?: StatusBadgeSize;
  /**
   * Show the icon paired with the variant. Defaults to `true`. Set
   * to `false` when the surrounding UI already provides a visual cue
   * (avoid icon noise in dense layouts).
   */
  withIcon?: boolean;
  /**
   * Override the variant's default icon. Use for context-specific
   * glyphs (e.g. BGP "Up" / "Down" arrows) while keeping the variant
   * colors and overall pill geometry.
   */
  icon?: React.ReactNode;
  /** Optional extra Tailwind classes. */
  className?: string;
}

/**
 * Background + text token pair per variant.
 *
 * Note on `info`: `themes.css` does not declare dedicated info tokens
 * today (only success / warning / danger / brand). Reusing the brand
 * tokens keeps the component theme-adaptive without hardcoding raw
 * primitives. When design ships
 * `--color-semantic-bg-info-secondary` + `--color-semantic-text-info`,
 * update this entry only — no call sites need to change.
 */
const VARIANT_CLASSES: Record<StatusBadgeVariant, string> = {
  success: "bg-semantic-bg-success-secondary text-semantic-text-success",
  warning: "bg-semantic-bg-warning-secondary text-semantic-text-warning",
  error: "bg-semantic-bg-danger-secondary text-semantic-text-danger",
  info: "bg-semantic-bg-brand-secondary text-semantic-text-brand",
  neutral: "bg-semantic-bg-secondary text-semantic-text-secondary",
};

const SIZE_CLASSES: Record<
  StatusBadgeSize,
  { container: string; icon: string }
> = {
  sm: {
    container: "gap-1 px-2 py-0.5 text-xs",
    icon: "w-3 h-3",
  },
  md: {
    container: "gap-1.5 px-2.5 py-1 text-sm",
    icon: "w-3.5 h-3.5",
  },
};

/**
 * Default icon per variant. `info` and `neutral` intentionally have no
 * default — there is no canonical asset for either today, and a missing
 * icon reads as "informational color, no decoration" instead of forcing
 * an arbitrary glyph. Consumers that need an icon for those variants
 * should pass one through the `icon` prop.
 */
const DEFAULT_ICON: Partial<
  Record<StatusBadgeVariant, React.FC<React.SVGProps<SVGSVGElement>>>
> = {
  success: IconSuccess,
  warning: IconWarning,
  error: IconCross,
};

/**
 * Compact pill that communicates the state of an entity in tables, card
 * headers and detail views. Standardizes the look originated by the
 * NotificationsV2 `HealthBadge` so every "Status" column in the app
 * speaks the same visual language.
 *
 * Companion to {@link GenericTag}:
 *  - Use `StatusBadge` for status / health UX (terse, colored chip,
 *    optionally iconed, non-interactive).
 *  - Use `GenericTag` when you need editable, removable, tooltip-portal,
 *    custom colors, click handlers or non-status semantics.
 *
 * Example:
 * ```tsx
 * <StatusBadge variant="success" label="Active" />
 * <StatusBadge variant="warning" label="No recipients" />
 * <StatusBadge variant="error" label="Down" />
 * <StatusBadge variant="success" label="Up" icon={<ArrowUp />} />
 * ```
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  label,
  size = "sm",
  withIcon = true,
  icon,
  className = "",
}) => {
  const sizeMap = SIZE_CLASSES[size];
  const variantClass = VARIANT_CLASSES[variant];

  let resolvedIcon: React.ReactNode = null;
  if (withIcon) {
    if (icon !== undefined) {
      resolvedIcon = icon;
    } else {
      const IconComponent = DEFAULT_ICON[variant];
      if (IconComponent) {
        resolvedIcon = (
          <IconComponent
            className={`${sizeMap.icon} flex-shrink-0`}
            aria-hidden
          />
        );
      }
    }
  }

  return (
    <span
      className={`inline-flex items-center font-medium rounded-sm ${sizeMap.container} ${variantClass} ${className}`}
    >
      {resolvedIcon}
      <span>{label}</span>
    </span>
  );
};

export default StatusBadge;
